import React, { useCallback, useEffect, useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Check, X, LogOut, Lock } from "lucide-react";
import { useLang } from "../i18n";
import { SymbolIcon } from "../components/SymbolIcon";
import { LangToggle } from "../components/Header";
import api from "../lib/api";

function Login({ onLogin }) {
  const { t } = useLang();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const { data } = await api.post("/auth/login", { email, password });
      localStorage.setItem("avsp_admin_token", data.token);
      onLogin();
    } catch (err) {
      setError(t("login_error"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-softpink px-5">
      <motion.form
        initial={{ opacity: 0, y: 14 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        onSubmit={submit}
        data-testid="admin-login-form"
        className="w-full max-w-sm rounded-panel bg-white p-8 shadow-soft"
      >
        <div className="flex h-11 w-11 items-center justify-center rounded-full bg-softpink text-dusty">
          <Lock size={20} strokeWidth={1.5} />
        </div>
        <h1 className="mt-5 text-2xl font-semibold tracking-tight text-graphite">{t("admin_title")}</h1>
        <p className="mt-1 text-sm text-textsec">{t("admin_sub")}</p>

        <label className="mt-6 block text-sm font-medium text-graphite">{t("email")}</label>
        <input
          data-testid="admin-email-input"
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          className="mt-2 w-full rounded-btn bg-softpink px-4 py-2.5 text-sm text-graphite outline-none ring-dusty/40 focus:ring-2"
          required
        />
        <label className="mt-4 block text-sm font-medium text-graphite">{t("password")}</label>
        <input
          data-testid="admin-password-input"
          type="password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          className="mt-2 w-full rounded-btn bg-softpink px-4 py-2.5 text-sm text-graphite outline-none ring-dusty/40 focus:ring-2"
          required
        />
        {error && (
          <p data-testid="admin-login-error" className="mt-3 text-sm text-danger">
            {error}
          </p>
        )}
        <button
          data-testid="admin-login-btn"
          type="submit"
          disabled={loading}
          className="mt-6 w-full rounded-btn bg-dusty px-5 py-3 text-sm font-medium text-white shadow-soft transition-colors hover:bg-dusty-dark disabled:opacity-50"
        >
          {loading ? t("signing_in") : t("sign_in")}
        </button>
      </motion.form>
    </div>
  );
}

function Dashboard({ onLogout }) {
  const { t, lang } = useLang();
  const [pending, setPending] = useState([]);
  const [summary, setSummary] = useState({ pending: 0, approved: 0, rejected: 0 });

  const load = useCallback(async () => {
    try {
      const [p, s] = await Promise.all([
        api.get("/admin/stories", { params: { status: "pending" } }),
        api.get("/admin/summary"),
      ]);
      setPending(p.data);
      setSummary(s.data);
    } catch (e) {
      if (e?.response?.status === 401) onLogout();
    }
  }, [onLogout]);

  useEffect(() => {
    load();
  }, [load]);

  const act = async (id, action) => {
    try {
      await api.post(`/admin/stories/${id}/${action}`);
      toast.success(action === "approve" ? t("approved") : t("rejected"));
      load();
    } catch (e) {
      toast.error(t("error_toast"));
    }
  };

  return (
    <div className="min-h-screen bg-softpink px-5 py-10 sm:px-8">
      <div className="mx-auto max-w-4xl">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-semibold tracking-tight text-graphite">{t("admin_title")}</h1>
            <p className="mt-1 text-sm text-textsec">{t("admin_sub")}</p>
          </div>
          <div className="flex items-center gap-3">
            <LangToggle />
            <button
              data-testid="admin-logout-btn"
              onClick={onLogout}
              className="inline-flex items-center gap-2 rounded-full bg-white px-4 py-2 text-sm font-medium text-graphite shadow-soft hover:text-dusty-dark"
            >
              <LogOut size={16} strokeWidth={1.75} />
              {t("logout")}
            </button>
          </div>
        </div>

        <div className="mt-6 grid grid-cols-3 gap-3">
          {[
            { k: "pending", label: t("pending"), v: summary.pending },
            { k: "approved", label: t("approved"), v: summary.approved },
            { k: "rejected", label: t("rejected"), v: summary.rejected },
          ].map((s) => (
            <div key={s.k} data-testid={`summary-${s.k}`} className="rounded-panel bg-white p-4 text-center shadow-soft">
              <div className="text-2xl font-semibold text-graphite">{s.v}</div>
              <div className="mt-0.5 text-xs text-textsec">{s.label}</div>
            </div>
          ))}
        </div>

        <div className="mt-8 space-y-4" data-testid="pending-list">
          {pending.length === 0 && (
            <div className="rounded-panel bg-white p-10 text-center text-sm text-textsec shadow-soft">
              {t("no_pending")}
            </div>
          )}
          {pending.map((s) => (
            <motion.div
              key={s.id}
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4 }}
              data-testid={`pending-item-${s.id}`}
              className="flex items-start gap-4 rounded-panel bg-white p-5 shadow-soft"
            >
              <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-softpink text-dusty">
                <SymbolIcon name={s.symbol} size={18} />
              </span>
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-dusty">
                  {t(`cat_${s.category}`)}
                </div>
                <p className="mt-1.5 text-sm leading-relaxed text-graphite">
                  {s.text && s.text.trim() ? (
                    s.text
                  ) : (
                    <span className="italic text-textsec">anonymous</span>
                  )}
                </p>
                <div className="mt-2 text-xs text-textsec">
                  {s.nickname || "Anonymous"} ·{" "}
                  {new Date(s.created_at).toLocaleDateString(lang === "ru" ? "ru-RU" : "en-US")}
                </div>
              </div>
              <div className="flex shrink-0 gap-2">
                <button
                  data-testid={`approve-${s.id}`}
                  onClick={() => act(s.id, "approve")}
                  className="flex h-9 w-9 items-center justify-center rounded-full bg-dusty text-white transition-colors hover:bg-dusty-dark"
                  title={t("approve")}
                >
                  <Check size={17} strokeWidth={2} />
                </button>
                <button
                  data-testid={`reject-${s.id}`}
                  onClick={() => act(s.id, "reject")}
                  className="flex h-9 w-9 items-center justify-center rounded-full bg-softpink text-textsec transition-colors hover:text-danger"
                  title={t("reject")}
                >
                  <X size={17} strokeWidth={2} />
                </button>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </div>
  );
}

export default function Admin() {
  const [authed, setAuthed] = useState(() => !!localStorage.getItem("avsp_admin_token"));

  const logout = () => {
    localStorage.removeItem("avsp_admin_token");
    setAuthed(false);
  };

  return authed ? <Dashboard onLogout={logout} /> : <Login onLogin={() => setAuthed(true)} />;
}
